import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { fadeInUp, staggerContainer, DURATION, EASE } from "@/lib/animations";
import { motion } from "framer-motion";
import { Link } from "wouter";
import {
  Brain,
  Globe,
  Handshake,
  Palette,
  Server,
  Smartphone,
  ArrowRight,
  ArrowUpRight
} from "lucide-react";

const services = [
  {
    icon: Smartphone,
    title: "mobileAppDev",
    description: "mobileAppDesc",
    technologies: ["Flutter", "Dart", "Firebase"],
    color: "primary",
    number: "01",
  },
  {
    icon: Server,
    title: "backendDev",
    description: "backendDevDesc",
    technologies: ["Go", "PostgreSQL", "Docker"],
    color: "secondary",
    number: "02",
  },
  {
    icon: Globe,
    title: "webDev",
    description: "webDevDesc",
    technologies: ["React", "TypeScript", "Tailwind"],
    color: "primary",
    number: "03",
  },
  {
    icon: Brain,
    title: "aiSolutions",
    description: "aiSolutionsDesc",
    badge: "comingSoon",
    technologies: ["Python", "TensorFlow", "OpenAI"],
    color: "accent",
    number: "04",
  },
  {
    icon: Palette,
    title: "designBranding",
    description: "designBrandingDesc",
    technologies: ["Figma", "Adobe Suite", "Motion"],
    color: "secondary",
    number: "05",
  },
  {
    icon: Handshake,
    title: "consulting",
    description: "consultingDesc",
    technologies: ["Strategy", "Assessment", "Planning"],
    color: "primary",
    number: "06",
  },
];

export default function Services() {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';

  return (
    <section id="services" className="relative py-24 md:py-32 bg-muted/30 overflow-hidden">
      {/* Background Geometric Elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Large decorative circle */}
        <motion.div
          className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full border border-primary/5"
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: EASE.smooth }}
        />
        <motion.div
          className="absolute -bottom-20 -left-20 w-[300px] h-[300px] rounded-full border border-secondary/5"
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.2, ease: EASE.smooth }}
        />

        {/* Floating dots */}
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-primary/20 rounded-full" />
        <div className="absolute top-1/3 right-1/4 w-3 h-3 bg-secondary/15 rounded-full" />
        <div className="absolute bottom-1/3 left-1/3 w-2 h-2 bg-accent/20 rounded-full" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16 md:mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: DURATION.slow }}
          viewport={{ once: true }}
        >
          <motion.span
            className="inline-block text-sm font-medium text-primary uppercase tracking-wider mb-4"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            {isRTL ? "ما نقدمه" : "What We Offer"}
          </motion.span>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            {t("servicesTitle")} <span className="gradient-text">{t("servicesTitleHighlight")}</span>
          </h2>

          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {t("servicesDescription")}
          </p>
        </motion.div>

        {/* Services Grid - Bento Style */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
        >
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              variants={fadeInUp}
              className={`group relative ${index === 0 || index === 3 ? 'md:col-span-2 lg:col-span-1' : ''}`}
            >
              <div
                className="relative h-full p-6 md:p-8 rounded-2xl bg-card border border-border/50 hover:border-primary/30 transition-all duration-300 overflow-hidden"
              >

                {/* Content */}
                <div className="relative z-10">
                  {/* Title with Icon */}
                  <div
                    className="flex items-center gap-4 mb-4"
                    style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                  >
                    {/* Icon */}
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/15 group-hover:scale-105 transition-all duration-300">
                      <service.icon className="h-6 w-6 text-primary" />
                    </div>

                    {/* Title with Badge */}
                    <div
                      className="flex items-center gap-2 flex-wrap"
                      style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                    >
                      <h3 className={`text-xl font-semibold text-foreground ${isRTL ? 'text-right' : ''}`}>
                        {t(service.title)}
                      </h3>
                      {service.badge && (
                        <Badge
                          variant="secondary"
                          className="text-xs bg-accent/10 text-accent border-0 shrink-0"
                        >
                          {t(service.badge)}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <p className={`text-muted-foreground leading-relaxed mb-6 ${isRTL ? 'text-right' : ''}`}>
                    {t(service.description)}
                  </p>

                  {/* Technologies */}
                  {/* <div className={`flex flex-wrap gap-2 mb-6 ${isRTL ? 'justify-end' : ''}`}>
                    {service.technologies.map((tech) => (
                      <span
                        key={tech}
                        className="text-xs px-3 py-1.5 rounded-lg bg-muted/80 text-muted-foreground font-medium"
                      >
                        {tech}
                      </span>
                    ))}
                  </div> */}

                  {/* Learn More Link */}
                  {/* <div style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
                    <button
                      className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors group/link"
                      style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                    >
                      {t("learnMore")}
                      <ArrowUpRight className="h-4 w-4 transition-transform group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                    </button>
                  </div> */}
                </div>

                {/* Hover gradient overlay */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                  style={{
                    background: `radial-gradient(600px circle at var(--mouse-x, 50%) var(--mouse-y, 50%), hsl(var(--primary) / 0.03), transparent 40%)`,
                  }}
                />
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: DURATION.slow, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <Link href="/services">
            <Button
              size="lg"
              variant="outline"
              className="group h-12 px-8 rounded-xl border-2 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300"
            >
              {t("viewAllServices") || "View All Services"}
              <ArrowRight className={`h-4 w-4 transition-transform group-hover:translate-x-1 ${isRTL ? 'rotate-180 mr-2 ml-0' : 'ml-2'}`} />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
